import { Link, Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from 'design-system/react';

import { getSimulation } from '../../client';
import { SimulationContext } from '../../SimulationContext';
import { TeamContext } from '../../TeamContext';
import { AppFeatureFlagsProvider } from '../../AppFeatureFlagsProvider';
import { useSimulationEvents } from '../../hooks/useSimulationEvents';
import { SimulationResultHandler } from '../hoc';
import { Page } from '../organisms';
import { AppShell, type NavItem } from './AppShell';

export function ParticipantLayout() {
  const { simId, teamName } = useParams<{ simId: string; teamName: string }>();

  if (!simId || !teamName) {
    return (
      <Page title="Error" hideBackButton>
        <p className="text-feedback-error mt-8 text-center">Invalid URL — missing simulation or team name.</p>
        <Link to="/" className="text-sm text-action-primary hover:underline mt-4 block text-center">
          &larr; Go to join page
        </Link>
      </Page>
    );
  }

  return <ParticipantLayoutInner simId={simId} teamName={decodeURIComponent(teamName)} />;
}

function ParticipantLayoutInner({ simId, teamName }: { simId: string; teamName: string }) {
  useSimulationEvents(simId);

  const {
    data: simulation,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['simulation', simId],
    queryFn: () => getSimulation({ path: { sim_id: simId }, throwOnError: true }).then(({ data }) => data),
    retry: 1,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <Page title="Loading..." hideBackButton>
        <div className="flex flex-col items-center gap-3 mt-8">
          <Spinner size="xl" />
          <p className="text-text-secondary">Loading simulation...</p>
        </div>
      </Page>
    );
  }

  if (isError || !simulation) {
    return (
      <Page title="Not Found" hideBackButton>
        <div className="flex flex-col items-center gap-4 mt-8">
          <p className="text-feedback-error">No simulation found.</p>
          <Link to="/" className="text-sm text-action-primary hover:underline">
            &larr; Try a different name
          </Link>
        </div>
      </Page>
    );
  }

  const encodedTeam = encodeURIComponent(teamName);
  const basePath = `/sim/${simId}/${encodedTeam}`;
  const items: NavItem[] = [
    { label: 'Tasks', to: `${basePath}/tasks` },
    { label: 'Features', to: `${basePath}/features`, featureFlag: 'features' },
  ];

  return (
    <SimulationContext.Provider value={{ simId: simulation.id, label: simulation.label }}>
      <TeamContext.Provider value={{ teamName }}>
        <AppFeatureFlagsProvider>
          <SimulationResultHandler>
            <AppShell
              roleLabel={
                <>
                  <samp className="font-bold">{teamName}</samp> team
                </>
              }
              items={items}
            >
              <Outlet />
            </AppShell>
          </SimulationResultHandler>
        </AppFeatureFlagsProvider>
      </TeamContext.Provider>
    </SimulationContext.Provider>
  );
}
